// Turns table rows into the lines of a time series chart: one series per area or route, each a list
// of UTC instants with a value or a gap. Pure: no React, router or fetch, and nothing here reads
// the machine's time zone.
import { parseBucket } from './dateWindow';

export interface SeriesPoint {
  /** ISO instant, UTC. */
  t: string;
  /** null = no figure for that instant (a gap in the line, not zero). */
  value: number | null;
}

export interface ChartSeries {
  key: string;
  label: string;
  /** Ascending by `t`, one point per instant. */
  points: SeriesPoint[];
}

const byKey = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });

/**
 * Groups rows by `key` and sorts each group's points by time. A row whose time cannot be read is
 * left out. The same (key, instant) can arrive twice when consecutive delivery batches overlap
 * (Latvia, Lithuania): as in dedupeFlowRows, the larger figure is kept and a null counts as smaller
 * than any number. Series are sorted by key, with numbers inside keys compared as numbers.
 */
export function buildChartSeries<R>(
  rows: readonly R[],
  f: {
    key: (r: R) => string;
    label: (r: R) => string;
    time: (r: R) => string;
    value: (r: R) => number | null;
  },
): ChartSeries[] {
  const groups = new Map<string, { label: string; at: Map<number, number | null> }>();
  for (const r of rows) {
    const ms = parseBucket(f.time(r));
    if (ms === null) continue;
    const key = f.key(r);
    let group = groups.get(key);
    if (!group) {
      group = { label: f.label(r), at: new Map() };
      groups.set(key, group);
    }
    const raw = f.value(r);
    const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : null; // undefined and NaN are gaps too
    const seen = group.at.get(ms);
    if (!group.at.has(ms)) group.at.set(ms, value);
    else if (value != null && (seen == null || value > seen)) group.at.set(ms, value);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => byKey(a, b))
    .map(([key, { label, at }]) => ({
      key,
      label,
      points: [...at.entries()].sort(([a], [b]) => a - b).map(([ms, value]) => ({ t: new Date(ms).toISOString(), value })),
    }));
}

const total = (s: ChartSeries): number => s.points.reduce((sum, p) => sum + (p.value ?? 0), 0);

/** Keeps the `max` series with the largest sum of figures (a tie goes to the earlier key), in their original order. */
export function limitSeries(series: ChartSeries[], max: number): { shown: ChartSeries[]; total: number } {
  if (series.length <= max) return { shown: series, total: series.length };
  const sums = new Map(series.map((s) => [s.key, total(s)] as const));
  const keep = new Set(
    [...series]
      .sort((a, b) => (sums.get(b.key) as number) - (sums.get(a.key) as number) || byKey(a.key, b.key))
      .slice(0, Math.max(0, max))
      .map((s) => s.key),
  );
  return { shown: series.filter((s) => keep.has(s.key)), total: series.length };
}
